import { lazy, Suspense, useEffect } from "react";
import { Switch, Route, useLocation } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useAuth } from "@/hooks/useAuth";
import { Sidebar } from "@/components/ui/sidebar";
import ErrorBoundary from "./pages/ErrorBoundatry/ErrorBoundary";

const NotFound = lazy(() => import("@/pages/not-found"));
const Landing = lazy(() => import("@/pages/landing"));
const Home = lazy(() => import("@/pages/home"));
const VendorRegistration = lazy(() => import("@/pages/vendor-registration"));
const Dashboard = lazy(() => import("@/pages/dashboard"));
const RegistrationStatus = lazy(() => import("@/pages/registration-status"));
const PurchaseOrders = lazy(() => import("@/pages/purchase-orders"));
const Payments = lazy(() => import("@/pages/payments"));
const SupplierRegistration = lazy(
  () => import("@/pages/supplier-registration"),
);
const SupplierLogin = lazy(() => import("@/pages/supplier-login"));
const SupplierDashboard = lazy(() => import("@/pages/supplier-dashboard"));
const SupplierProfile = lazy(() => import("@/pages/supplier-profile"));
const ApproverLogin = lazy(() => import("@/pages/approver-login"));
const SimpleApproverDashboard = lazy(
  () => import("@/pages/simple-approver-dashboard"),
);
const SupplierDetailsView = lazy(() => import("@/pages/supplier-details-view"));
const SupplierReview = lazy(() => import("@/pages/supplier-review"));
const UnifiedSignIn = lazy(() => import("@/pages/unified-signin"));
const SupplierPricelist = lazy(() => import("./pages/supplier-pricelist"));

function Router() {
  const { isAuthenticated, isLoading } = useAuth();
  const [, setLocation] = useLocation();

  useEffect(() => {
    if (typeof window === "undefined") return;

    const INACTIVITY_MS = 3 * 60 * 60 * 1000; // 3 hours
    const CHECK_INTERVAL_MS = 60 * 1000; // 1 minute
    const LAST_ACTIVITY_KEY = "APP_LAST_ACTIVITY_TS";

    const hasActiveSession = () =>
      Boolean(localStorage.getItem("SUPPLIER_ID")) ||
      Boolean(localStorage.getItem("approverId"));

    const markActivity = () => {
      if (!hasActiveSession()) return;
      localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
    };

    const clearSessionData = () => {
      localStorage.setItem("SESSION_EXPIRED_NOTICE", "1");
      localStorage.removeItem("SUPPLIER_ID");
      localStorage.removeItem("supplierDetails");
      localStorage.removeItem("approverId");
      localStorage.removeItem("empId");
      localStorage.removeItem("VID");
      localStorage.removeItem("login-response");
      localStorage.removeItem(LAST_ACTIVITY_KEY);
    };

    const checkInactivity = () => {
      const supplierSession = Boolean(localStorage.getItem("SUPPLIER_ID"));
      const approverSession = Boolean(localStorage.getItem("approverId"));

      if (!supplierSession && !approverSession) return;

      const lastActivity = Number(localStorage.getItem(LAST_ACTIVITY_KEY) || 0);

      if (!lastActivity) {
        markActivity();
        return;
      }

      if (Date.now() - lastActivity >= INACTIVITY_MS) {
        clearSessionData();
        setLocation("/supplier-login");
      }
    };

    const activityEvents = [
      "mousemove",
      "mousedown",
      "keydown",
      "scroll",
      "touchstart",
      "click",
    ];

    checkInactivity();
    activityEvents.forEach((eventName) =>
      window.addEventListener(eventName, markActivity, { passive: true }),
    );

    const intervalId = window.setInterval(checkInactivity, CHECK_INTERVAL_MS);

    return () => {
      activityEvents.forEach((eventName) =>
        window.removeEventListener(eventName, markActivity),
      );
      window.clearInterval(intervalId);
    };
  }, [setLocation]);

  const routes =
    isLoading || !isAuthenticated ? (
      <Switch>
        <Route path="/" component={UnifiedSignIn} />
        <Route path="/" component={SupplierLogin} />
        <Route path="/signin" component={UnifiedSignIn} />
        <Route path="/landing" component={Landing} />
        <Route path="/vendor-registration" component={VendorRegistration} />
        <Route path="/supplier-registration" component={SupplierRegistration} />
        <Route path="/supplier-pricelist" component={SupplierPricelist} />
        <Route path="/supplier-login" component={SupplierLogin} />
        <Route path="/supplier-dashboard" component={SupplierDashboard} />
        <Route path="/supplier-profile" component={SupplierProfile} />
        <Route path="/approver-login" component={ApproverLogin} />
        <Route path="/approver-dashboard" component={SimpleApproverDashboard} />
        <Route path="/supplier-details/:vid" component={SupplierDetailsView} />
        <Route
          path="/supplier-review/:supplierVid"
          component={SupplierReview}
        />
        <Route component={NotFound} />
      </Switch>
    ) : (
      <div className="flex h-screen bg-gray-50">
        <Sidebar />
        <main className="flex-1 overflow-auto md:ml-64">
          <Switch>
            <Route path="/" component={Dashboard} />
            <Route path="/dashboard" component={Dashboard} />
            <Route path="/registration-status" component={RegistrationStatus} />
            <Route path="/purchase-orders" component={PurchaseOrders} />
            <Route path="/payments" component={Payments} />
            <Route path="/vendor-registration" component={VendorRegistration} />
            <Route
              path="/supplier-registration"
              component={SupplierRegistration}
            />
            <Route
              path="/supplier-dashboard/:id"
              component={SupplierDashboard}
            />
            <Route path="/supplier-profile" component={SupplierProfile} />
            <Route component={NotFound} />
          </Switch>
        </main>
      </div>
    );

  return <Suspense fallback={<div>Loading...</div>}>{routes}</Suspense>;
}

function ErrorFallback({ error }) {
  return (
    <div role="alert" className="p-4 text-red-800 bg-red-100">
      <p>Something went wrong:</p>
      <pre>{error.message}</pre>
    </div>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <ErrorBoundary FallbackComponent={ErrorFallback}>
          <Router />
        </ErrorBoundary>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
